import {
  CardPosition,
  EnginePhase,
  EventType,
  type GameEvent,
  type MatchState,
  type Seat,
} from '@shared/index'

/** Battle step parked, or the AI never moved, with nothing new on the board. */
export const STALL_QUIET_MS = 12_000

/**
 * One payload can hold a long battle animation. The scene already force-unlocks
 * a missed timer a few seconds after that animation. Past this, playback is hung.
 */
export const STALL_PLAYBACK_MS = 20_000

export type BattleStallReason = 'playback' | 'substep' | 'prompt' | 'ai'

export type BattleStallHit = {
  reason: BattleStallReason
  step: string
  pending: string
  turn: 'me' | 'opp'
  responder: 'me' | 'opp' | 'none'
  cpu: boolean
  turnN: number
  signature: string
}

export type StallView = {
  /** HUD phase while a payload is still animating; otherwise the drawn board. */
  phase: EnginePhase
  mySeat: Seat
  playbackLocked: boolean
  /** Trap prompt, field menu, picker, or position overlay is on screen. */
  promptVisible: boolean
  /** Local target or attack pick. The player is choosing, not stuck. */
  selecting: boolean
}

/**
 * Why this Battle Phase snapshot looks frozen.
 *
 * A player deciding an attack, and a human opponent with a visible "waiting"
 * prompt, are not stalls. A missing prompt, a battle substep that nobody can
 * answer, a playback lock, or an AI seat that stopped are.
 */
export function detectBattleStall(
  state: MatchState,
  view: StallView,
): BattleStallHit | null {
  if (state.status !== 'active') return null
  if (view.phase !== EnginePhase.BATTLE) return null
  if (view.selecting) return null

  const owed = owedSeat(state)
  const waitingOnMe = owed === view.mySeat
  const cpu = state.seats[0].isAI || state.seats[1].isAI
  const waitingOnAi = owed !== null && state.seats[owed].isAI
  const aiTurn =
    state.seats[state.turnPlayer].isAI && owed === null && !view.promptVisible
  const parked =
    (state.battleStep !== null && state.battleStep !== 'BATTLE') ||
    state.pendingAttack !== null ||
    state.chain.length > 0

  let reason: BattleStallReason | null = null
  if (view.playbackLocked) reason = 'playback'
  else if (waitingOnAi || aiTurn) reason = 'ai'
  else if (view.promptVisible && !waitingOnMe) reason = null
  else if (waitingOnMe && !view.promptVisible) reason = 'prompt'
  else if (parked && !view.promptVisible) reason = 'substep'

  if (!reason) return null

  const step = state.battleStep ?? 'none'
  const pending = pendingKind(state)
  const turn = state.turnPlayer === view.mySeat ? 'me' : 'opp'
  const responder = seatSide(owed, view.mySeat)
  return {
    reason,
    step,
    pending,
    turn,
    responder,
    cpu,
    turnN: state.absoluteTurnCount,
    signature: `${reason}|${step}|${pending}|${turn}|${responder}`,
  }
}

/** How long this reason has to sit with no events before it is reported. */
export function stallQuietLimit(reason: BattleStallReason): number {
  return reason === 'playback' ? STALL_PLAYBACK_MS : STALL_QUIET_MS
}

function owedSeat(state: MatchState): Seat | null {
  if (state.pendingPriority) return state.pendingPriority.responder
  if (state.pendingDiscard) return state.pendingDiscard.seat
  if (state.pendingFlipEffect) return state.pendingFlipEffect.chooser
  if (state.pendingTargets) return state.pendingTargets.chooser
  if (state.pendingCoinCall) return state.pendingCoinCall.chooser
  if (state.pendingChoice) return state.pendingChoice.chooser
  if (state.pendingChainOrder) return state.pendingChainOrder.chooser
  if (state.pendingOfferingSummon !== null) return state.pendingOfferingSummon
  return null
}

/** The other player owes the open choice (response, target, discard, Offering). */
export function isWaitingOnOpponent(state: MatchState, mySeat: Seat): boolean {
  const owed = owedSeat(state)
  return owed !== null && owed !== mySeat
}

function pendingKind(state: MatchState): string {
  if (state.pendingPriority) return 'priority'
  if (state.pendingAttack) return 'attack'
  if (state.pendingDiscard) return 'discard'
  if (state.pendingFlipEffect) return 'flip'
  if (state.pendingTargets) return 'targets'
  if (state.pendingCoinCall) return 'coin'
  if (state.pendingChoice) return 'choice'
  if (state.pendingChainOrder) return 'chainOrder'
  if (state.pendingOfferingSummon !== null) return 'offering'
  if (state.chain.length > 0) return 'chain'
  return 'none'
}

function seatSide(seat: Seat | null, mySeat: Seat): 'me' | 'opp' | 'none' {
  if (seat === null) return 'none'
  return seat === mySeat ? 'me' : 'opp'
}

const STALL_REPORT_CAP = 4
const RECENT_CAP = 8
const PROP_MAX = 180

export type StallDebug = {
  prompt: string
  atk: string
  chain: string
  field: string
  back: string
  recent: string
}

/**
 * Compact snapshot of the frozen battle: passcodes, not names.
 * Ops turns the passcodes into card names when it renders the row.
 */
export function stallDebug(
  state: MatchState,
  mySeat: Seat,
  recent: readonly string[],
): StallDebug {
  const opp = (mySeat === 0 ? 1 : 0) as Seat
  const mine = state.zones[mySeat]
  const theirs = state.zones[opp]
  return {
    prompt: clip(stallPrompt(state)),
    atk: clip(stallAttack(state)),
    chain: clip(stallChain(state)),
    field: clip(
      `me ${slots(mine.monsters, true)} | opp ${slots(theirs.monsters, true)}`,
    ),
    back: clip(
      `me ${slots(mine.spellTraps, false)} | opp ${slots(theirs.spellTraps, false)} | F ${fieldToken(mine.fieldSpell)}/${fieldToken(theirs.fieldSpell)}`,
    ),
    recent: clip(recent.join(' ')),
  }
}

/** One short token per engine event, with passcodes resolved at arrival. */
export function stallEventToken(
  event: GameEvent,
  idOf: (uid: string) => string | null,
): string | null {
  const id = (uid: string | null | undefined, hidden = 'set'): string => {
    if (!uid) return hidden
    return idOf(uid) || hidden
  }
  switch (event.type) {
    case EventType.ATTACK_DECLARED:
      return `ATK:${id(event.attackerUid, '?')}>${event.targetUid ? id(event.targetUid) : 'direct'}`
    case EventType.ATTACK_REPLAY:
      return `RPL:${id(event.attackerUid, '?')}>${event.targetUid ? id(event.targetUid) : 'direct'}`
    case EventType.BATTLE_RESOLVED: {
      const flags = [
        event.attackerDestroyed ? 'atkDead' : '',
        event.targetDestroyed ? 'defDead' : '',
      ].filter(Boolean)
      const tail = flags.length ? `(${flags.join(',')})` : ''
      return `BAT:${id(event.attackerUid, '?')}>${event.targetUid ? id(event.targetUid) : 'direct'}${tail}`
    }
    case EventType.REQUEST_PRIORITY:
      return `PRI:${event.promptCode}`
    case EventType.REQUEST_TARGETS:
      return `TGT:${event.sourceCardId || id(event.sourceUid, '?')}`
    case EventType.TRAP_ACTIVATED:
      return `TRP:${event.cardId}`
    case EventType.SPELL_ACTIVATED:
      return `SPL:${event.cardId}`
    case EventType.CARD_FLIPPED:
      return `FLP:${event.cardId || id(event.cardUid)}`
    case EventType.CARD_SUMMONED:
      return `SUM:${event.cardId}`
    case EventType.CARD_SET:
      return `SET:${id(event.cardUid)}`
    case EventType.CARD_DESTROYED:
      return `DES:${id(event.cardUid, '?')}`
    case EventType.CARD_TO_GRAVEYARD:
      return `GY:${id(event.cardUid, '?')}`
    case EventType.PHASE_CHANGED:
      return `PH:${event.phase}`
    case EventType.EFFECT_RESOLVED:
      return `EFF:${id(event.cardUid, '?')}`
    case EventType.MESSAGE:
      return event.code
        ? `MSG:${event.code}${event.vars?.cardId ? '@' + event.vars.cardId : ''}`
        : null
    default:
      return null
  }
}

function stallPrompt(state: MatchState): string {
  const priority = state.pendingPriority
  if (priority) {
    const trig = priority.triggerUid ? cardIdOf(state, priority.triggerUid) : null
    const link = priority.chainLink ? `#${priority.chainLink}` : ''
    return `${priority.promptCode}${trig ? `@${trig}` : ''}${link}`
  }
  if (state.pendingFlipEffect) return `flip:${state.pendingFlipEffect.cardId}`
  if (state.pendingTargets) return `pick:${state.pendingTargets.cardId}`
  if (state.pendingCoinCall) return `coin:${state.pendingCoinCall.cardId}`
  if (state.pendingChainOrder) {
    return `order:${state.pendingChainOrder.cards.map((card) => card.cardId).join(',')}`
  }
  if (state.pendingDiscard) return `discard:${state.pendingDiscard.count}`
  if (state.pendingOfferingSummon !== null) return 'offering'
  return ''
}

function stallAttack(state: MatchState): string {
  const attack = state.pendingAttack
  if (!attack) return ''
  const attacker = cardIdOf(state, attack.attackerUid) || '?'
  const target = attack.targetUid ? cardIdOf(state, attack.targetUid) || 'set' : 'direct'
  return `${attacker}>${target} ${attack.stage}`
}

function stallChain(state: MatchState): string {
  const links = state.chain
    .map((link) => link.cardId || cardIdOf(state, link.sourceUid) || '?')
    .join('>')
  const legal = (state.pendingPriority?.activatableUids ?? [])
    .map((uid) => cardIdOf(state, uid))
    .filter((id): id is string => !!id)
  const parts = [links, legal.length ? `can:${legal.join(',')}` : ''].filter(Boolean)
  return parts.join(' ')
}

function slots(
  cards: MatchState['zones'][Seat]['monsters'],
  monster: boolean,
): string {
  return cards
    .map((card) => {
      if (!card) return '.'
      if (!card.faceUp || !card.cardId) return 'set'
      if (!monster) return card.cardId
      const pos = card.position === CardPosition.ATTACK ? 'a' : 'd'
      return `${card.cardId}${pos}${card.attackedThisTurn ? '*' : ''}`
    })
    .join(',')
}

function fieldToken(card: MatchState['zones'][Seat]['fieldSpell']): string {
  if (!card) return '-'
  if (!card.faceUp || !card.cardId) return 'set'
  return card.cardId
}

function cardIdOf(state: MatchState, uid: string): string | null {
  for (const zone of state.zones) {
    const piles = [
      ...zone.monsters,
      ...zone.spellTraps,
      zone.fieldSpell,
      ...zone.hand,
      ...zone.graveyard,
      ...zone.banished,
    ]
    for (const card of piles) {
      if (card && card.uid === uid && card.cardId) return String(card.cardId)
    }
  }
  return null
}

function clip(value: string): string {
  if (value.length <= PROP_MAX) return value
  const cut = value.slice(0, PROP_MAX)
  const space = cut.lastIndexOf(' ')
  return space > 40 ? cut.slice(0, space) : cut
}

/**
 * Quiet time on a visible duel. Time spent in a background tab does not count,
 * so coming back from chat does not look like a freeze.
 */
export class DuelStallWatch {
  private quietAccum = 0
  private mark = Date.now()
  private reported = new Set<string>()
  private recent: string[] = []
  private timer: ReturnType<typeof setInterval> | null = null
  private onVisibility = (): void => {
    if (document.visibilityState === 'hidden') {
      this.quietAccum += Math.max(0, Date.now() - this.mark)
      return
    }
    this.mark = Date.now()
  }

  start(check: () => void): void {
    this.stop()
    this.noteProgress()
    document.addEventListener('visibilitychange', this.onVisibility)
    this.timer = setInterval(check, 2_000)
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer)
    this.timer = null
    document.removeEventListener('visibilitychange', this.onVisibility)
  }

  /** A new snapshot or event batch arrived. The board is moving. */
  noteProgress(): void {
    this.quietAccum = 0
    this.mark = Date.now()
  }

  /** New duel. The same freeze shape may be reported again. */
  clearClaims(): void {
    this.reported.clear()
    this.recent.length = 0
    this.noteProgress()
  }

  /** Remember the public tail of the duel so a freeze names the cards involved. */
  noteEvents(events: GameEvent[], idOf: (uid: string) => string | null): void {
    for (const event of events) {
      const token = stallEventToken(event, idOf)
      if (!token) continue
      this.recent.push(token)
    }
    if (this.recent.length > RECENT_CAP) {
      this.recent.splice(0, this.recent.length - RECENT_CAP)
    }
  }

  debug(state: MatchState, mySeat: Seat): StallDebug {
    return stallDebug(state, mySeat, this.recent)
  }

  quietMs(): number {
    if (document.visibilityState === 'hidden') return this.quietAccum
    return this.quietAccum + Math.max(0, Date.now() - this.mark)
  }

  /** True the first time this signature is seen, up to a few per duel. */
  claim(signature: string): boolean {
    if (this.reported.has(signature)) return false
    if (this.reported.size >= STALL_REPORT_CAP) return false
    this.reported.add(signature)
    return true
  }
}
